import { cancelFunctions } from 'utils/cancelFunctions';
import { CreativeStatus } from 'components/common/types/Creative.types';
import { PermissionsEnum } from 'consts/permissions';
import Auth from 'modules/Auth';
import { differenceWith, isEqual } from 'lodash';
import { addCreativeCategory, removeCreativeCategory } from 'modules/api/content';
import { setAdditionalProductCategories } from 'store/creative/reducer';
import { useDispatch, useSelector } from 'react-redux';
import { Store } from 'components/common/types/Store.types';
import BaseSelect from 'lib/BaseSelect';
import usePendoTracking from 'customHooks/usePendoTracking';
import Chip, { Color, Width } from 'lib/Chip';
import {
  AdditionalProductCategoriesProps,
  RemoveProductCategoryHandler,
  SelectProductCategoriesHandler,
} from './AdditionalProductCategories.types';

const AdditionalProductCategories = ({
  creativeStatus,
  isDisabled = false,
  marketId,
  onTaggingRulesChange,
}: AdditionalProductCategoriesProps): React.ReactElement => {
  const { getPendoDataId } = usePendoTracking();
  const dispatch = useDispatch();
  const additionalProductCategories = useSelector((store: Store) => store.creative.additionalProductCategories);
  const additionalProductCategoriesOptions = useSelector(
    (store: Store) => store.creative.additionalProductCategoriesOptions,
  );
  const hasEditPermission = Auth.hasPermission(PermissionsEnum.CONTENT_CREATIVE_CATEGORIES_EDIT);

  const isProductCategoryDisabled =
    creativeStatus === CreativeStatus.REJECTED ||
    creativeStatus === CreativeStatus.APPROVED ||
    !hasEditPermission ||
    additionalProductCategoriesOptions.length === 0 ||
    isDisabled;

  const handleSelectProductCategories: SelectProductCategoriesHandler = async (_dropdownName, values) => {
    const categoryIdToRemove = differenceWith(additionalProductCategories, values, isEqual)[0]?.id;
    const categoryIdToAdd = differenceWith(values, additionalProductCategories, isEqual)[0]?.id;

    try {
      if (categoryIdToRemove) {
        await removeCreativeCategory(cancelFunctions, marketId, categoryIdToRemove);
      } else if (categoryIdToAdd) {
        await addCreativeCategory(cancelFunctions, marketId, categoryIdToAdd);
      }

      onTaggingRulesChange();
      dispatch(setAdditionalProductCategories(values));
      // eslint-disable-next-line no-empty
    } catch {}
  };

  const handleRemoveProductCategory: RemoveProductCategoryHandler = (selectedProductCategory) => {
    const updatedSelectedCategories = additionalProductCategories.filter(
      (category) => selectedProductCategory.id !== category.id,
    );

    dispatch(setAdditionalProductCategories(updatedSelectedCategories));
    handleSelectProductCategories('', updatedSelectedCategories);
  };

  return (
    <div className="row">
      <div className="col-12 col-md-4" data-test-id="additional-product-categories">
        <BaseSelect
          dataTestId="additional-product-categories-input"
          name="categories"
          placeholder="Select a category"
          isMultiple
          isDisabled={isProductCategoryDisabled}
          onSelect={handleSelectProductCategories}
          selectedValue={additionalProductCategories}
          options={additionalProductCategoriesOptions}
          closeMenuOnSelect={false}
          isSelectionVisible={false}
          withPortal={false}
          isSearchable
          dataPendoId={getPendoDataId('Additional Product Categories')}
        />
      </div>
      <div
        className="col-12 col-md-8 flex flex-wrap gap-x-7 gap-y-1"
        data-test-id="additional-product-categories-chips"
      >
        {additionalProductCategories.map((category) => (
          <Chip
            color={Color.PRIMARY}
            onRemove={() => handleRemoveProductCategory(category)}
            isDisabled={isProductCategoryDisabled}
            label={category.name}
            dataTestId={`additional-product-categories-chip-${category.code}`}
            width={Width.FULL}
            truncateLabel={false}
          />
        ))}
      </div>
    </div>
  );
};

export default AdditionalProductCategories;
